import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Pencil, Trash2, Plus, Upload, Save, X, FileUp } from 'lucide-react';
import DataTable from '../components/DataTable';
import SearchBar from '../components/SearchBar';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import ConfirmDialog from '../components/ConfirmDialog';
import DateRangeFilter from '../components/DateRangeFilter';
import DateInput from '../components/DateInput';
import { useLanguage } from '../context/LanguageContext';
import { PAID_TYPES, UNPAID_TYPES, AUTO_PTO } from '../absenceTypes';
import AbsenceRequests from './AbsenceRequests';

const EMPTY_FORM = { badge_num: '', id_emp_abs: '', start_date_abs: '', end_date_abs: '', type_abs: '', status_abs: '', pto_abs: 0 };

export default function Absences() {
  const { t } = useLanguage();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [reason, setReason] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [empName, setEmpName] = useState('');
  const [empSearch, setEmpSearch] = useState('');
  const [empResults, setEmpResults] = useState([]);
  const [importData, setImportData] = useState(null);
  const [importing, setImporting] = useState(false);
  // 'absences' = lo que ya esta en el sistema; 'requests' = solicitudes aprobadas en la otra app.
  const [tab, setTab] = useState('absences');
  const [pendingCount, setPendingCount] = useState(0);
  const fileRef = useRef();

  // Burbuja del boton "Solicitudes aprobadas". Si falla solo se esconde: no vale un error en pantalla.
  const fetchPendingCount = useCallback(async () => {
    try {
      // Marcada como polling para que no renueve la sesion: no es actividad del usuario.
      const res = await axios.get('/api/absences/requests/pending-count',
        { headers: { 'X-Background-Poll': '1' } });
      setPendingCount(res.data.count || 0);
    } catch { setPendingCount(0); }
  }, []);

  // Cada 15 s, para que una solicitud que el manager aprueba con la pantalla abierta aparezca sin
  // recargar. No mas seguido: cada consulta ocupa una de las 10 conexiones del pool que comparten
  // todas las pantallas. Con la pestana del navegador oculta no se consulta; al volver se pone al dia.
  useEffect(() => {
    fetchPendingCount();
    const timer = setInterval(() => { if (!document.hidden) fetchPendingCount(); }, 15000);
    const onVisible = () => { if (!document.hidden) fetchPendingCount(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchPendingCount]);
  const limit = 50;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/absences', { params: { employee: search, dateFrom, dateTo, page, limit } });
      setData(res.data.data);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: t('common.error'), text: t('abs.fetchError') || 'Error loading absences.' });
    }
    setLoading(false);
  }, [search, dateFrom, dateTo, page, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const lookupEmployee = async (badge) => {
    if (!badge) { setEmpName(''); setForm(f => ({ ...f, id_emp_abs: '' })); return; }
    try {
      const res = await axios.get(`/api/employees/badge/${badge}`);
      setEmpName(`${res.data.name_emp} ${res.data.last_name_emp}`);
      setForm(f => ({ ...f, id_emp_abs: res.data.id_emp }));
    } catch {
      setEmpName(t('abs.notFound'));
      setForm(f => ({ ...f, id_emp_abs: '' }));
    }
  };

  const searchEmployees = async (query) => {
    setEmpSearch(query);
    if (!query || query.length < 2) { setEmpResults([]); return; }
    try {
      const res = await axios.get('/api/employees', { params: { search: query } });
      const list = Array.isArray(res.data) ? res.data : (res.data.data || []);
      setEmpResults(list.slice(0, 8));
    } catch { setEmpResults([]); }
  };

  const selectEmployee = (emp) => {
    setForm(f => ({ ...f, badge_num: String(emp.badge_num_emp), id_emp_abs: emp.id_emp }));
    setEmpName(`${emp.name_emp} ${emp.last_name_emp}`);
    setEmpSearch(`${emp.name_emp} ${emp.last_name_emp}`);
    setEmpResults([]);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setEmpName('');
    setEmpSearch('');
    setEmpResults([]);
    setReason('');
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      badge_num: row.badge_num_emp || '',
      id_emp_abs: row.id_emp_abs,
      start_date_abs: row.start_date_abs ? row.start_date_abs.substring(0, 10) : '',
      end_date_abs: row.end_date_abs ? row.end_date_abs.substring(0, 10) : '',
      type_abs: row.type_abs || '',
      status_abs: row.status_abs || '',
      pto_abs: row.pto_abs || 0,
    });
    setEmpName(row.name_abs ? `${row.name_abs} ${row.last_name_abs}` : '');
    setEmpSearch('');
    setEmpResults([]);
    setReason('');
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.id_emp_abs) { Swal.fire({ icon: 'warning', title: t('abs.invalidEmp'), text: t('abs.invalidEmpMsg') }); return; }
    if (form.end_date_abs && form.start_date_abs && form.end_date_abs < form.start_date_abs) {
      Swal.fire({ icon: 'warning', title: t('abs.invalidDates'), text: t('abs.invalidDatesMsg') }); return;
    }
    if (editing && !reason.trim()) {
      Swal.fire({ icon: 'warning', title: t('audit.reasonRequired') || 'Razon requerida', text: t('audit.reasonRequiredMsg') || 'Indica el motivo del cambio.' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        id_emp_abs: form.id_emp_abs,
        start_date_abs: form.start_date_abs,
        end_date_abs: form.end_date_abs || form.start_date_abs,
        type_abs: form.type_abs,
        status_abs: form.status_abs,
        pto_abs: form.pto_abs,
        reason: editing ? reason : undefined,
      };
      if (editing) {
        await axios.put(`/api/absences/${editing.id_abs}`, payload);
      } else {
        await axios.post('/api/absences', payload);
      }
      setModalOpen(false);
      fetchData();
      Swal.fire({ icon: 'success', title: editing ? t('abs.updated') : t('abs.created'), timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('common.saveError') });
    }
    setSaving(false);
  };

  const askDelete = async (id) => {
    const { value: reasonInput } = await Swal.fire({
      title: t('abs.deleteConfirm') || 'Eliminar ausencia?',
      input: 'textarea',
      inputLabel: t('audit.reasonRequired') || 'Razon (obligatoria)',
      inputPlaceholder: t('audit.reasonPh') || 'Por que se esta eliminando?',
      inputValidator: (v) => !v?.trim() && (t('audit.reasonRequiredMsg') || 'La razon es obligatoria.'),
      showCancelButton: true,
      confirmButtonText: t('common.delete') || 'Eliminar',
      cancelButtonText: t('common.cancel') || 'Cancelar',
      confirmButtonColor: '#ef4444',
    });
    if (!reasonInput) return;
    setDeleteReason(reasonInput);
    setDeleteId(id);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/api/absences/${deleteId}`, { data: { reason: deleteReason } });
      setDeleteId(null);
      setDeleteReason('');
      fetchData();
      // Borrar una ausencia que vino de una solicitud la regresa a "por agregar".
      fetchPendingCount();
      Swal.fire({ icon: 'success', title: t('abs.deleted'), timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('common.deleteError') });
    }
  };

  // Convert any date value to YYYY-MM-DD (American storage format)
  // Siempre usa fecha LOCAL para evitar shift de día por UTC conversion.
  const toLocalISODate = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  const parseDate = (val) => {
    if (!val) return '';
    // Date object
    if (val instanceof Date) {
      return toLocalISODate(val);
    }
    // Excel serial number (e.g. 46113)
    if (typeof val === 'number') {
      const epoch = new Date(1899, 11, 30);
      const d = new Date(epoch.getTime() + val * 86400000);
      return toLocalISODate(d);
    }
    const s = String(val).trim();
    // Already YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    // MM/DD/YYYY or M/D/YYYY (American)
    const usMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (usMatch) {
      const [, m, d, y] = usMatch;
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    // DD-MM-YYYY or DD.MM.YYYY
    const euMatch = s.match(/^(\d{1,2})[-.](\d{1,2})[-.](\d{4})$/);
    if (euMatch) {
      const [, d, m, y] = euMatch;
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    // YYYY/MM/DD
    const isoSlash = s.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
    if (isoSlash) {
      const [, y, m, d] = isoSlash;
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    // Fallback: try native Date parsing
    const parsed = new Date(s);
    if (!isNaN(parsed.getTime())) return toLocalISODate(parsed);
    return s;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const XLSX = await import('xlsx');
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { raw: false, dateNF: 'yyyy-mm-dd' });
      if (!Array.isArray(json) || json.length === 0) {
        Swal.fire({ icon: 'warning', title: t('common.emptyFile'), text: t('common.emptyFileMsg') });
        e.target.value = '';
        return;
      }
      // Normalize all date columns in preview
      const dateKeys = ['start_date_abs', 'date_abs', 'date', 'Fecha', 'end_date_abs'];
      const normalized = json.map(row => {
        const newRow = { ...row };
        for (const key of dateKeys) {
          if (newRow[key]) newRow[key] = parseDate(newRow[key]);
        }
        return newRow;
      });
      setImportData(normalized);
      setImportModalOpen(true);
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: t('abs.fileError') });
      console.error(err);
    }
    e.target.value = '';
  };

  const handleImport = async () => {
    if (!importData || importData.length === 0) return;
    setImporting(true);
    try {
      const absences = importData.map(row => {
        const rawStart = row.start_date_abs || row.date_abs || row.date || row.Fecha;
        const rawEnd = row.end_date_abs || rawStart;
        return {
          id_emp_abs: row.id_emp_abs || row.id_emp || row.ID,
          badge: row.badge || row.Badge || row.badge_num_emp || '',
          start_date_abs: parseDate(rawStart),
          end_date_abs: parseDate(rawEnd),
          type_abs: row.type_abs || row.type || row.Tipo || '',
          status_abs: row.status_abs || row.status || row.Status || '',
          pto_abs: row.pto_abs || row.pto || row.PTO || 0,
        };
      });
      const res = await axios.post('/api/absences/import', { absences });
      const skippedMsg = res.data.skipped ? `\n${res.data.skipped} ${t('abs.importSkipped')} ${res.data.skippedIds.join(', ')}` : '';
      Swal.fire({ icon: res.data.skipped ? 'warning' : 'success', title: t('abs.importComplete'), text: `${res.data.inserted} ${t('abs.importedMsg')}${skippedMsg}` });
      setImportModalOpen(false);
      setImportData(null);
      fetchData();
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('abs.importError') });
    }
    setImporting(false);
  };

  const formatDate = (d) => {
    if (!d) return '';
    const s = typeof d === 'string' ? d.substring(0, 10) : d;
    const dt = new Date(s + (typeof s === 'string' && s.length === 10 ? 'T00:00:00' : ''));
    return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-US');
  };

  const columns = [
    { header: t('common.badge'), accessor: 'badge_num_emp' },
    { header: t('abs.employee2'), render: (r) => r.name_abs ? `${r.name_abs} ${r.last_name_abs}` : '-' },
    { header: t('abs.dept'), accessor: 'department_abs' },
    { header: t('abs.startDateCol'), render: (r) => formatDate(r.start_date_abs) },
    { header: t('abs.endDateCol'), render: (r) => formatDate(r.end_date_abs) },
    { header: t('common.type'), accessor: 'type_abs' },
    { header: 'PTO', render: (r) => r.pto_abs || 0 },
    { header: t('common.status'), render: (r) => <span className={`badge ${r.status_abs === 'Approved' ? 'badge-success' : r.status_abs === 'Pending' ? 'badge-warning' : 'badge-info'}`}>{r.status_abs || '-'}</span> },
    {
      header: t('common.actions'),
      style: { width: 120 },
      render: (r) => (
        <div className="table-actions">
          <button className="btn-secondary btn-sm" onClick={() => openEdit(r)}><Pencil size={14} style={{ marginRight: 4 }} />{t('common.edit')}</button>
          <button className="btn-danger btn-sm" onClick={() => askDelete(r.id_abs)}><Trash2 size={14} style={{ marginRight: 4 }} />{t('common.delete')}</button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>{t('abs.title')}</h1>
        {tab === 'absences' && (
          <div className="page-header-actions">
            <button className="btn-secondary" style={{ whiteSpace: 'nowrap' }} onClick={() => fileRef.current?.click()}><Upload size={15} style={{ marginRight: 6, flexShrink: 0 }} />{t('common.importExcel')}</button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFileUpload} />
            <button className="btn-primary" style={{ width: 'auto', padding: '8px 20px', whiteSpace: 'nowrap' }} onClick={openCreate}><Plus size={16} style={{ marginRight: 4, flexShrink: 0 }} />{t('common.newFem')}</button>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button className={tab === 'absences' ? 'btn-primary' : 'btn-secondary'} style={{ width: 'auto', padding: '8px 18px' }}
          onClick={() => setTab('absences')}>{t('abs.tabAbsences')}</button>
        <button className={tab === 'requests' ? 'btn-primary' : 'btn-secondary'} style={{ width: 'auto', padding: '8px 18px' }}
          onClick={() => setTab('requests')}>
          {t('abs.tabRequests')}
          {pendingCount > 0 && (
            <span title={t('abs.pendingBubble')} style={{
              marginLeft: 8, minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10,
              background: 'var(--danger)', color: '#fff', fontSize: 12, fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
            }}>{pendingCount}</span>
          )}
        </button>
      </div>

      {tab === 'requests' ? (
        <AbsenceRequests onApplied={() => { fetchData(); fetchPendingCount(); }} />
      ) : (
        <>
          <div className="filters-bar">
            <SearchBar value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder={t('abs.search')} />
            <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} onDateFromChange={(v) => { setDateFrom(v); setPage(1); }} onDateToChange={(v) => { setDateTo(v); setPage(1); }} onRefresh={() => { setDateFrom(''); setDateTo(''); setPage(1); }} />
          </div>

          <div className="table-container">
            <DataTable columns={columns} data={data} loading={loading} emptyMessage={t('abs.empty')} />
            <Pagination page={page} limit={limit} total={total} onPageChange={setPage} />
          </div>
        </>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? t('abs.editTitle') : t('abs.newTitle')}>
        <form onSubmit={handleSave}>
          <div className="modal-body">
            {/* Employee search section */}
            <div style={{
              padding: 16, marginBottom: 16,
              background: 'var(--bg-tertiary)', borderRadius: 'var(--radius)',
              border: empName && empName !== t('abs.notFound') ? '2px solid var(--success)' : '1px solid var(--border)',
            }}>
              <label className="form-label" style={{ marginBottom: 8, display: 'block' }}>{t('abs.employee')}</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="form-input"
                  placeholder={t('abs.employeeSearch')}
                  value={empSearch}
                  onChange={e => {
                    searchEmployees(e.target.value);
                    if (/^\d+$/.test(e.target.value)) {
                      setForm(f => ({ ...f, badge_num: e.target.value }));
                    }
                  }}
                  onBlur={() => {
                    if (/^\d+$/.test(empSearch) && !form.id_emp_abs) lookupEmployee(empSearch);
                    setTimeout(() => setEmpResults([]), 200);
                  }}
                  style={{ fontSize: 15 }}
                />
                {empResults.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                    background: 'var(--bg-primary)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)', maxHeight: 220, overflowY: 'auto',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                  }}>
                    {empResults.map(emp => (
                      <div key={emp.id_emp}
                        onMouseDown={() => selectEmployee(emp)}
                        style={{
                          padding: '10px 14px', cursor: 'pointer',
                          borderBottom: '1px solid var(--border)',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <span style={{ fontWeight: 600 }}>{emp.name_emp} {emp.last_name_emp}</span>
                        <span className="badge badge-info" style={{ fontSize: 11 }}>Badge {emp.badge_num_emp}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {empName && empName !== t('abs.notFound') && (
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="badge badge-success" style={{ fontSize: 12, padding: '4px 10px' }}>Badge {form.badge_num}</span>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{empName}</span>
                </div>
              )}
              {empName === t('abs.notFound') && (
                <small style={{ color: 'var(--danger)', marginTop: 6, display: 'block' }}>{t('abs.notFound')}</small>
              )}
            </div>

            {/* Dates */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('abs.startDate')}</label>
                <DateInput className="form-input" required value={form.start_date_abs}
                  onChange={v => setForm({ ...form, start_date_abs: v, end_date_abs: form.end_date_abs || v })} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('abs.endDate')}</label>
                <DateInput className="form-input" value={form.end_date_abs}
                  min={form.start_date_abs}
                  onChange={v => setForm({ ...form, end_date_abs: v })} />
              </div>
            </div>

            {/* Type + Status + PTO */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('abs.typeReq')}</label>
                <select className="form-input" value={form.type_abs} onChange={e => {
                  const type = e.target.value;
                  setForm({ ...form, type_abs: type, pto_abs: AUTO_PTO[type] ?? form.pto_abs });
                }}>
                  <option value="">{t('common.select')}</option>
                  <optgroup label={t('abs.paid')}>
                    {PAID_TYPES.map(ty => <option key={ty} value={ty}>{ty}</option>)}
                  </optgroup>
                  <optgroup label={t('abs.unpaid')}>
                    {UNPAID_TYPES.map(ty => <option key={ty} value={ty}>{ty}</option>)}
                  </optgroup>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t('common.status')}</label>
                <select className="form-input" value={form.status_abs} onChange={e => setForm({ ...form, status_abs: e.target.value })}>
                  <option value="">{t('common.select')}</option>
                  <option value="Approved">Approved</option>
                  <option value="Pending">Pending</option>
                  <option value="Denied">Denied</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t('abs.ptoHrs')}</label>
                {/* step="any" a proposito: sin `step` el input usa 1 y el navegador rechaza 6.5,
                    asi que media jornada no se podia capturar aunque la columna ya acepte
                    decimales. "any" no impone cuartos ni medias horas — la fraccion la decide quien
                    registra la ausencia. */}
                <input className="form-input" type="number" min="0" step="any" value={form.pto_abs}
                  onChange={e => setForm({ ...form, pto_abs: Number(e.target.value) })}
                  placeholder="0" />
              </div>
            </div>

            {editing && (
              <div className="form-row">
                <div className="form-group" style={{ width: '100%' }}>
                  <label className="form-label">
                    {t('audit.reason') || 'Razon'} <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <textarea
                    className="form-input"
                    rows={2}
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder={t('audit.reasonPh') || 'Por que se esta editando?'}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}><X size={15} style={{ marginRight: 4 }} />{t('common.cancel')}</button>
            <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '8px 24px' }} disabled={saving || !form.id_emp_abs}>
              <Save size={15} style={{ marginRight: 4 }} />{saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={importModalOpen} onClose={() => { setImportModalOpen(false); setImportData(null); }} title={t('abs.importTitle')} large>
        <div className="modal-body">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
            <span style={{ fontSize: 28, lineHeight: 1 }}>&#128203;</span>
            <div>
              <p style={{ margin: 0, fontWeight: 600, fontSize: 15 }}>
                <strong>{importData?.length || 0}</strong> {t('abs.importRecords')}
              </p>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                {t('abs.importCols')}
              </p>
            </div>
          </div>
          {importData && importData.length > 0 && (
            <div className="table-wrapper" style={{ maxHeight: 300, overflowY: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    {Object.keys(importData[0]).map(key => <th key={key}>{key}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {importData.slice(0, 10).map((row, i) => (
                    <tr key={i}>
                      {Object.values(row).map((val, j) => <td key={j}>{String(val)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
              {importData.length > 10 && <p className="text-center" style={{ padding: 8, color: 'var(--text-muted)' }}>+ {importData.length - 10} {t('abs.importMore')}</p>}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={() => setImportModalOpen(false)}><X size={15} style={{ marginRight: 4 }} />{t('common.cancel')}</button>
          <button className="btn-primary" style={{ width: 'auto', padding: '8px 20px' }} onClick={handleImport} disabled={importing}>
            <FileUp size={15} style={{ marginRight: 4 }} />{importing ? t('common.importing') : `${t('rep.import')} ${importData?.length || 0} ${t('rep.importRecords')}`}
          </button>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        message={t('abs.deleteConfirm')}
      />
    </div>
  );
}
